<template>
  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="9"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Product Catalog
            </h2>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <feather-icon
              icon="SearchIcon"
              size="22"
              class="mr-50"
            />
            <span class="align-middle mr-50">Search</span>
            <b-input-group class="input-group-merge mr-50">
              <b-form-input
                v-model="searchQuery"
                placeholder="Package Name"
              />
              <b-input-group-append
                v-if="searchQuery"
                is-text
              >
                <feather-icon
                  icon="XIcon"
                  class="cursor-pointer"
                  @click="searchQuery = ''"
                />
              </b-input-group-append>
            </b-input-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-dropdown
                id="dropdown-catalog-filter"
                ref="filter_dropdown_catalog"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="dropdown-modal"
                right
                variant="link"
              >
                <template #button-content>
                  <feather-icon
                    icon="FilterIcon"
                    size="21"
                    class="align-middle mr-50"
                  />
                  <span class="mr-1">Filter</span>
                </template>
                <catalog-filter
                  :category-filter.sync="categoryFilter"
                  :status-filter.sync="statusFilter"
                  :created-at.sync="createdAtFilter"
                  @emitHideFilter="hideFilter"
                  @emitShowFilter="showFilter"
                />
              </b-dropdown>
            </div>
          </b-col>
        </b-row>

        <div v-if="categoryFilter.length || statusFilter || createdAtFilter">
          <hr class="dividerHR filter-divider">
          <b-row>
            <b-col
              cols="12"
              md="12"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <ul class="list-inline d-inline-block mb-1">
                <li
                  v-if="createdAtFilter"
                  class="list-inline-item"
                >
                  <b-form-tag
                    class="mb-50"
                    variant="danger"
                    @remove="createdAtFilter = ''"
                  >
                    {{ createdAtFilter }}
                  </b-form-tag>
                </li>
                <li
                  v-if="statusFilter"
                  class="list-inline-item"
                >
                  <b-form-tag
                    class="mb-50"
                    variant="danger"
                    @remove="statusFilter = ''"
                  >
                    {{ statusFilter }}
                  </b-form-tag>
                </li>
                <li
                  v-for="cat in categoryFilter"
                  :key="cat"
                  class="list-inline-item"
                >
                  <b-form-tag
                    class="mb-50"
                    variant="danger"
                    @remove="removeCategoryFilter(cat)"
                  >
                    {{ cat }}
                  </b-form-tag>
                </li>
                <li class="list-inline-item">
                  <span
                    class="text-danger clear-filter-text mb-50"
                    @click="clearAllFilters"
                  >
                    Clear Filters
                  </span>
                </li>
              </ul>
            </b-col>
          </b-row>
        </div>
      </div>

      <b-table
        ref="refCatalogsTable"
        class="position-relative min-height-table"
        :items="itemListData"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        :sort-by.sync="sortBy"
        show-empty
        no-sort-reset
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- ID -->
        <template #cell(stringID)="data">
          <div class="text-nowrap">
            <b-button
              v-if="canViewThisAction('show', 'ProductCatalog')"
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="link"
              class="weight-600 align-middle detail-view-id"
              :to="{ name: 'products-catalog-show', params: { id: data.item._id } }"
            >
              {{ data.item.stringID }}
            </b-button>
            <b-button
              v-else
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="none"
              class="text-bold-black align-middle detail-view-id not-button"
            >
              {{ data.item.stringID }}
            </b-button>
          </div>
        </template>

        <!-- Image -->
        <template #cell(image)="data">
          <b-img
            class="mb-1 mb-sm-0 user__avatar img-preview-block new-block"
            :src="data.item.image ? data.item.image : defaultImage"
          />
        </template>

        <!-- Category -->
        <template #cell(category)="data">
          <span v-if="data.item.category">{{ data.item.category.name }}</span>
          <span v-else>—</span>
        </template>

        <!-- Product Codes -->
        <template #cell(productCodes)="data">
          <span
            v-for="(pc, index) in data.item.productCodes"
            :key="pc._id"
          >
            <b-button
              v-if="canViewThisAction('show', 'ProductCode')"
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="link"
              class="p-0 detail-view-id"
              :to="{ name: 'products-codes-show', params: { id: pc._id } }"
            >{{ pc.code }}</b-button>
            <span v-else>{{ pc.code }}</span>
            <span v-if="index < data.item.productCodes.length - 1">, </span>
          </span>
          <span v-if="!data.item.productCodes || !data.item.productCodes.length">—</span>
        </template>

        <!-- Status -->
        <template #cell(status)="data">
          <b-badge
            :variant="data.item.status === 'On Sale' ? 'light-success' : 'light-secondary'"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Actions -->
        <template
          v-if="canViewThisAction('show', 'ProductCatalog') || canViewThisAction('update', 'ProductCatalog')"
          #cell(actions)="data"
        >
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('show', 'ProductCatalog')"
              :to="{ name: 'products-catalog-show', params: { id: data.item._id } }"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'ProductCatalog')"
              :to="{ name: 'products-catalog-edit', params: { id: data.item._id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div
      v-if="totalCatalogs > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCatalogs"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
            prev-text="Prev"
            next-text="Next"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BTable, BInputGroup, BInputGroupAppend,
  BImg, BDropdown, BDropdownItem, BPagination, BFormTag, BButton, BBadge,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import useCatalogList from './useCatalogList'
import catalogStoreModule from './catalogStoreModule'
import CatalogFilter from './CatalogFilter.vue'

export default {
  components: {
    BFormTag,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BInputGroupAppend,
    BImg,
    BButton,
    BBadge,
    CatalogFilter,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      // eslint-disable-next-line global-require
      defaultImage: require('@/assets/images/placeholder/placeholder_image.png'),
    }
  },
  methods: {
    hideFilter() {
      this.$refs.filter_dropdown_catalog.hide(true)
    },
    showFilter() {
      this.$refs.filter_dropdown_catalog.show(true)
    },
    clearAllFilters() {
      this.categoryFilter = []
      this.statusFilter = ''
      this.createdAtFilter = ''
    },
    removeCategoryFilter(cat) {
      this.categoryFilter = this.categoryFilter.filter(c => c !== cat)
    },
  },
  setup() {
    const CATALOG_APP_STORE_MODULE_NAME = 'app-product-catalog'

    if (!store.hasModule(CATALOG_APP_STORE_MODULE_NAME)) {
      store.registerModule(CATALOG_APP_STORE_MODULE_NAME, catalogStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(CATALOG_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(CATALOG_APP_STORE_MODULE_NAME)
      }
    })

    const { canViewThisAction } = useAclUtils()

    const {
      fetchCatalogs,
      tableColumns,
      perPage,
      currentPage,
      totalCatalogs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCatalogsTable,
      refetchData,
      currentDataLength,
      itemListData,
      categoryFilter,
      statusFilter,
      createdAtFilter,
    } = useCatalogList()

    return {
      fetchCatalogs,
      tableColumns,
      perPage,
      currentPage,
      totalCatalogs,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refCatalogsTable,
      refetchData,
      currentDataLength,
      itemListData,
      avatarText,
      canViewThisAction,
      categoryFilter,
      statusFilter,
      createdAtFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.img-preview-block {
  box-sizing: border-box;
  width: 38px;
  height: 38px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
}
</style>
